import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ORDER } from "../../../config";
import { InternalLabelData } from "../../../models/InternalLabelData";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import { Checkbox } from "../../FormElement/Checkbox";
import { InputBox } from "../../FormElement/InputBox";
import "./styles.scss";

interface ModalAssignFacilitiesProps {
  internalLabel: InternalLabelData;
  onClose: () => void;
  onUpdate: (result: { addIds: string[]; deleteIds: string[] }) => void;
}

const ModalAssignFacilities: React.FC<ModalAssignFacilitiesProps> = ({
  internalLabel,
  onClose,
  onUpdate,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`internalLabelsManagementPage.modalAssignFacilities.${key}`);

  const { data: locationsData } = useQuery(["locations"], () =>
    DataSvc.getLocations({
      sort: "id",
      limit: 10000,
      order: ORDER.ASC,
      numberPerPage: 1000,
      pageIndex: 1,
      summary_mode: true,
    })
  );

  const assignedLocationIds = React.useMemo(
    () =>
      (internalLabel.locations_internal_labels || []).map((item) =>
        String(item.locations.id)
      ),
    [internalLabel]
  );

  const [selected, setSelected] = useState<string[]>(assignedLocationIds);
  useEffect(() => {
    setSelected(assignedLocationIds);
  }, [assignedLocationIds]);

  const [searchName, setSearchName] = useState("");
  const [searchedName, setSearchedName] = useState("");

  const filteredLocations = React.useMemo(() => {
    if (!locationsData?.list) return [];
    if (!searchedName) return locationsData.list;
    return locationsData.list.filter((location: LocationData) =>
      location.name.toLowerCase().includes(searchedName.toLowerCase())
    );
  }, [locationsData, searchedName]);

  const handleCheck = (id: number, checked: boolean) => {
    const idStr = String(id);
    setSelected((prev) =>
      checked
        ? [...prev, idStr]
        : prev.filter((locationId) => locationId !== idStr)
    );
  };

  const handleSubmit = () => {
    const addIds = selected.filter((id) => !assignedLocationIds.includes(id));
    const deleteIds = assignedLocationIds.filter(
      (id) => !selected.includes(id)
    );
    onUpdate({ addIds, deleteIds });
  };

  return (
    <div className="modal modal-default modal-assign-facilities">
      <div className="modal-mains">
        <button
          className="btn-close"
          aria-label="Close"
          onClick={onClose}
        ></button>
        <div className="title">{t("title")}</div>
        <div className="filter-bar flex-grid">
          <InputBox
            value={searchName}
            placeholder={t("search_by_name")}
            isSearch={true}
            classNameContainer={"search-input"}
            onChange={(value: string) => {
              setSearchName(value);
            }}
            onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                setSearchedName(searchName);
              }
            }}
          />
        </div>
        <div className="content-selection-wrap">
          <div className="selection-wrap">
            {filteredLocations.map((location: LocationData) => (
              <div className="items" key={location.id}>
                <div className="checkbox-wrap have-txt">
                  <Checkbox
                    checked={selected.includes(String(location.id))}
                    label={location.name}
                    id={`checkbox-location-${location.id}`}
                    onChange={(checked: boolean) =>
                      handleCheck(location.id, checked)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bottom-btns">
          <button className="btn btn-border" onClick={onClose}>
            {t("cancel")}
          </button>
          <button className="btn btn-blue" onClick={handleSubmit}>
            {t("submit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalAssignFacilities;
